import Turnstile from '../components/Turnstile';
import React, { useState } from 'react';
import axios from 'axios';
import AuthLayout, { authInputStyle, authLabelStyle, authPrimaryButtonStyle } from '../components/AuthLayout';

/**
 * お問い合わせフォーム。
 *
 * 従来は mailto: リンクだった。メールソフトが設定されていない端末（特にスマホや
 * 共有PC）では何も起きず、問い合わせ経路が事実上存在しない状態になっていた。
 * フォームなら確実に届き、種別を選ばせることで一次対応も速くなる。
 */
const CATEGORIES = [
  { value: 'inquiry', label: '導入・料金のご相談' },
  { value: 'support', label: '不具合・使い方のご質問' },
  { value: 'other', label: 'その他' },
];

const ContactPage: React.FC = () => {
  const [form, setForm] = useState({
    name: '',
    companyName: '',
    email: '',
    category: 'inquiry',
    message: '',
    // ハニーポット。人間には見えないので、埋まっていれば自動投稿とみなす。
    website: '',
  });
  const [turnstileToken, setTurnstileToken] = useState('');
  const [sending, setSending] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSending(true);
    try {
      await axios.post('/api/contact', { ...form, turnstileToken });
      setDone(true);
    } catch (err: any) {
      setError(
        err.response?.data?.error ||
          '送信に失敗しました。お手数ですが info@kinlog.jp まで直接ご連絡ください。'
      );
    } finally {
      setSending(false);
    }
  };

  if (done) {
    return (
      <AuthLayout title="お問い合わせを受け付けました">
        <p style={{ color: '#334155', fontSize: '14px', lineHeight: 1.9, margin: 0 }}>
          ご入力のメールアドレス宛に、受付内容の控えをお送りしました。
          担当者より順次ご連絡いたしますので、しばらくお待ちください。
        </p>
        <p style={{ color: '#64748B', fontSize: '13px', marginTop: '14px' }}>
          控えのメールが届かない場合は、迷惑メールフォルダをご確認ください。
        </p>
        <a
          href="/"
          style={{ ...authPrimaryButtonStyle, display: 'block', textAlign: 'center', textDecoration: 'none', marginTop: '20px' }}
        >
          トップページへ戻る
        </a>
      </AuthLayout>
    );
  }

  return (
    <AuthLayout
      title="お問い合わせ"
      description="導入のご相談、料金、機能についてなど、お気軽にお問い合わせください。"
    >
      {error && (
        <div
          style={{
            padding: '12px',
            marginBottom: '16px',
            borderRadius: '8px',
            backgroundColor: '#FEF2F2',
            color: '#991B1B',
            border: '1px solid #FECACA',
            fontSize: '13px',
          }}
        >
          {error}
        </div>
      )}

      <form onSubmit={handleSubmit}>
        <div style={{ marginBottom: '14px' }}>
          <label htmlFor="category" style={authLabelStyle}>
            お問い合わせ種別
          </label>
          <select
            id="category"
            value={form.category}
            onChange={(e) => setForm({ ...form, category: e.target.value })}
            style={authInputStyle}
          >
            {CATEGORIES.map((c) => (
              <option key={c.value} value={c.value}>
                {c.label}
              </option>
            ))}
          </select>
        </div>

        <div style={{ marginBottom: '14px' }}>
          <label htmlFor="name" style={authLabelStyle}>
            お名前 <span style={{ color: '#DC2626' }}>*</span>
          </label>
          <input
            id="name"
            type="text"
            required
            maxLength={100}
            value={form.name}
            onChange={(e) => setForm({ ...form, name: e.target.value })}
            style={authInputStyle}
          />
        </div>

        <div style={{ marginBottom: '14px' }}>
          <label htmlFor="companyName" style={authLabelStyle}>
            会社名・屋号
          </label>
          <input
            id="companyName"
            type="text"
            maxLength={200}
            value={form.companyName}
            onChange={(e) => setForm({ ...form, companyName: e.target.value })}
            style={authInputStyle}
          />
        </div>

        <div style={{ marginBottom: '14px' }}>
          <label htmlFor="email" style={authLabelStyle}>
            メールアドレス <span style={{ color: '#DC2626' }}>*</span>
          </label>
          <input
            id="email"
            type="email"
            required
            maxLength={254}
            value={form.email}
            onChange={(e) => setForm({ ...form, email: e.target.value })}
            style={authInputStyle}
          />
        </div>

        <div style={{ marginBottom: '18px' }}>
          <label htmlFor="message" style={authLabelStyle}>
            お問い合わせ内容 <span style={{ color: '#DC2626' }}>*</span>
          </label>
          <textarea
            id="message"
            required
            rows={7}
            maxLength={4000}
            value={form.message}
            onChange={(e) => setForm({ ...form, message: e.target.value })}
            style={{ ...authInputStyle, resize: 'vertical', lineHeight: 1.7 }}
          />
        </div>

        {/* ハニーポット。実在の入力欄ではないため、見えない位置に置く。 */}
        <div style={{ position: 'absolute', left: '-9999px' }} aria-hidden>
          <label htmlFor="website">Website</label>
          <input
            id="website"
            type="text"
            tabIndex={-1}
            autoComplete="off"
            value={form.website}
            onChange={(e) => setForm({ ...form, website: e.target.value })}
          />
        </div>

        <Turnstile onToken={setTurnstileToken} />

        <button
          type="submit"
          disabled={sending}
          style={{ ...authPrimaryButtonStyle, opacity: sending ? 0.6 : 1, cursor: sending ? 'not-allowed' : 'pointer' }}
        >
          {sending ? '送信中...' : '送信する'}
        </button>

        <p style={{ fontSize: '12px', color: '#64748B', marginTop: '12px', lineHeight: 1.7 }}>
          ご入力いただいた情報は、お問い合わせへの回答のために利用します。取扱いについては
          <a href="/privacy" style={{ color: '#2563EB' }}>
            プライバシーポリシー
          </a>
          をご確認ください。
        </p>
      </form>
    </AuthLayout>
  );
};

export default ContactPage;
